import { useEffect, useState } from "react";
import { Heart, Flame, Users } from "lucide-react";
import { getKindnessThisWeek } from "@/lib/api";

function CountUp({ to, duration = 1200 }) {
    const [value, setValue] = useState(0);
    useEffect(() => {
        if (to <= 0) { setValue(0); return; }
        const start = performance.now();
        const initial = 0;
        let raf;
        const step = (t) => {
            const progress = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - progress, 3);
            setValue(Math.round(initial + (to - initial) * eased));
            if (progress < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [to, duration]);
    return <>{value.toLocaleString()}</>;
}

export default function KindnessTicker() {
    const [data, setData] = useState({ deeds: 0, points: 0, kids: 0 });
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const d = await getKindnessThisWeek();
                if (!cancelled) {
                    setData(d);
                    setLoaded(true);
                }
            } catch {
                if (!cancelled) setLoaded(true);
            }
        }
        load();
        // Re-check every 60s for live feel
        const t = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    return (
        <section className="border-y-2 border-ink bg-white" data-testid="kindness-ticker">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
                <div className="grid md:grid-cols-[auto_1fr] gap-8 items-center">
                    <div>
                        <span className="pill bg-mint">
                            <Flame className="w-3.5 h-3.5" strokeWidth={2.5} /> This week
                        </span>
                        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-3">
                            Going the second mile, together ✝️
                        </h2>
                        <p className="text-ink/70 mt-1">Anonymous, real-time tally from kids around the world.</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3 sm:gap-5">
                        <Card icon={Heart} color="bg-coral" dark label="Acts of kindness" value={data.deeds} loaded={loaded} />
                        <Card icon={Flame} color="bg-sun" label="Kindness points" value={data.points} loaded={loaded} />
                        <Card icon={Users} color="bg-sky" dark label="Active kids" value={data.kids} loaded={loaded} />
                    </div>
                </div>
            </div>
        </section>
    );
}

function Card({ icon: Icon, color, dark, label, value, loaded }) {
    return (
        <div className="card-sticker bg-cream p-4 sm:p-5 text-center sm:text-left">
            <div className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border-2 border-ink ${color} mb-2`}>
                <Icon className={`w-5 h-5 ${dark ? "text-white" : "text-ink"}`} strokeWidth={2.5} />
            </div>
            <p className="font-heading text-3xl sm:text-4xl font-bold leading-none">
                {loaded ? <CountUp to={value} /> : "—"}
            </p>
            <p className="text-xs uppercase tracking-widest font-bold text-ink/60 mt-1">{label}</p>
        </div>
    );
}
